import { useSelector } from 'react-redux'
import { useCallback, useEffect } from 'react'

import getRegAvailable from 'app/selectors/getRegAvailable'
import Transfer from 'app/components/Fund/Transfer'
import {
  BannerContainer,
  Refill,
  getIsNoSpaceForBanner,
  getIsReturnLinkAvailable,
} from 'app/components/Fund/Refill'
import {
  useAcquiringSettingsLoading,
  useAcquiringSettingsAvailability,
  useAcquiringSettingsRefillStyle,
} from 'app/dataProviders/acquiring/settings/hooks'
import { useTransferAvailability, useTransferLoading } from 'app/dataProviders/transfer/hooks'

import {
  FundSection,
  RefillStep,
  RefillTarget,
  RefillType,
  isFundSection,
  isRefillStep,
  isRefillTarget,
  isRefillType,
} from 'app/dataTypes/acquiring/settings'

import FundNavigation from '../../_common/FundNavigation'
import { values } from 'ramda'

const baseURL = '/finances/fund'

type Props = {
  params: {
    type?: string
    step?: string
    target?: string
    order?: string
    targetId?: string
  }
  router: {
    replace: (pathname: string) => void
    push: (pathname: string) => void
  }
}

const FundPage = ({ params, router }: Props) => {
  const { type, target, order, step, targetId = null } = params
  const { push } = router

  const fundSection = isFundSection(type) ? (type as FundSection) : null
  const refillStep = isRefillStep(step) ? (step as RefillStep) : null
  const refillTarget = isRefillTarget(target) ? (target as RefillTarget) : null
  const refillType = isRefillType(order) ? (order as RefillType) : null

  const createHomePath = useCallback((op: 'refill' | 'transfer') => {
    // finances/fund/:type(fundSection)/:target(refillTarget)/:order(refillType)/:step/:targetId
    if (op === 'refill') {
      return `${baseURL}/${FundSection.Refill}`
    }
    if (op === 'transfer') {
      return `${baseURL}/${FundSection.Transfer}`
    }
    return '' as never
  }, [])

  const redirectToDefault = useCallback(() => {
    push(`${baseURL}/${FundSection.Refill}`)
  }, [push])

  const handleChangeStep = useCallback(
    ({
      step: stp,
      target: trgt = refillTarget,
      type: tp = refillType,
      targetID: trgtID = targetId || undefined,
    }: {
      step: number
      target?: RefillTarget | null
      type?: RefillType | null
      targetID?: string
    }) => {
      const stepValue = [null, ...values(RefillStep)][stp]

      if (trgt && tp && stepValue) {
        const pathTo = `${baseURL}/${FundSection.Refill}/${trgt}/${tp}/${stepValue}`
        if (trgtID) {
          push(`${pathTo}/${trgtID}`)
          return
        }
        push(pathTo)
        return
      }
      redirectToDefault()
    },
    [push, refillTarget, refillType, redirectToDefault, targetId],
  )

  useEffect(() => {
    if (!fundSection) redirectToDefault()
  }, [fundSection]) // eslint-disable-line react-hooks/exhaustive-deps

  const regAvailable = useSelector(getRegAvailable)

  // возможность пополнения
  const refillAvailable = useAcquiringSettingsAvailability()
  const loadingRefill = useAcquiringSettingsLoading()

  // стиль пополнения
  const refillStyle = useAcquiringSettingsRefillStyle(refillType, targetId)

  const transferAvailable = useTransferAvailability()
  const showTransferLink = transferAvailable
  const loadingTransfer = useTransferLoading()

  const refillHomeURL = createHomePath('refill')
  const successURL = `${window.location.origin}${baseURL}/success`
  const failureURL = `${window.location.origin}${baseURL}/fail`
  const transferHomeURL = createHomePath('transfer')

  if (!fundSection) return null

  return (
    <>
      <FundNavigation
        activeFundSection={fundSection}
        showRefillLink={refillAvailable}
        refillLink={refillHomeURL}
        showTransferLink={showTransferLink}
        transferLink={transferHomeURL}
        showReturnLink={getIsReturnLinkAvailable(refillStep, refillStyle)}
        returnLink={refillHomeURL}
        loading={loadingRefill || loadingTransfer}
      />

      {(fundSection === FundSection.Refill ||
        fundSection === FundSection.Success ||
        fundSection === FundSection.Fail) &&
        refillAvailable && (
          <BannerContainer
            bannerPlaceID="d099e4c6-f44b-42ef-b5f9-a1d52c27dee5"
            hide={getIsNoSpaceForBanner(refillStep, refillStyle)}
          >
            <Refill
              cafeteriaOwnAccountRefillDisable
              payForAlerts={regAvailable}
              showSuccess={fundSection === FundSection.Success}
              showFail={fundSection === FundSection.Fail}
              refillStep={refillStep}
              refillTarget={refillTarget}
              refillType={refillType}
              targetID={targetId}
              homeURL={refillHomeURL}
              successURL={successURL}
              failureURL={failureURL}
              onChangeStep={handleChangeStep}
            />
          </BannerContainer>
        )}

      {fundSection === FundSection.Transfer && transferAvailable && <Transfer />}
    </>
  )
}

export default FundPage
